import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { AuthService } from '@app/auth/services/auth.service';
import { TokenStorageService } from '@app/auth/services/token-storage.service';
import { ToastService } from '@app/shared/components/toast/services/toast.service';

import {
  ForceLogoutReason,
  forceLogout,
  logout,
  logoutFailure,
  logoutSuccess,
} from './auth.actions';

/**
 * Centralized logout effects. Every logout flow — user clicks "logout",
 * the auth-interceptor gives up after a failed refresh, or the realtime
 * pipeline forces a kick — runs through here so the WebSocket is torn down,
 * tokens cleared, and the user routed to login exactly once.
 *
 * The realtime teardown is wired in `RealtimeEffects.disconnect$` which also
 * matches `logout` and `forceLogout`.
 */
@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      switchMap(({ source }) => {
        // The interceptor and realtime paths are firing because the session
        // is already gone server-side; skip the backend call to avoid a
        // guaranteed 401 toast on the way out.
        if (source !== 'user') {
          this.cleanup();
          return of(logoutSuccess());
        }
        return this.authService.logout().pipe(
          map(() => {
            this.cleanup();
            return logoutSuccess();
          }),
          catchError(error => {
            this.cleanup();
            return of(logoutFailure({ error }));
          })
        );
      })
    )
  );

  forceLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(forceLogout),
        tap(({ reason }) => {
          const message = this.translateReason(reason);
          if (message) {
            this.toast.error(message);
          }
          this.cleanup();
        })
      ),
    { dispatch: false }
  );

  private cleanup(): void {
    this.tokenStorage.clear();
    this.router.navigateByUrl('/auth/login');
  }

  private translateReason(reason: ForceLogoutReason): string {
    const key = `realtime.forceLogout.${reason}`;
    const translated = this.translate.instant(key);
    // ngx-translate returns the key verbatim when missing; fall back to
    // a generic message in that case.
    if (translated === key) {
      return this.translate.instant('realtime.forceLogout.generic');
    }
    return translated;
  }
}
